import { BusinessAnalytics } from "@xepp/business-analytics-sdk";
import { getCurrentUserId } from "../../guards/common/authentification-functions";
import type { DynamicFieldItem } from "../../model/dynamic-forms";
import type { ServiceRequestType } from "../../model/service-request/service-request";
import { FieldValue, withoutEmpty } from "./customFields";

/**
 * Report Issue events for the web app, mirroring the "Start Report Issue" category of the
 * shared analytics event sheet: opening the Service Request page, opening the Issue Tracker
 * complaint form and submitting the complaint.
 *
 * The flow and its steps are seeded by the backend migration SeedProductAnalyticsCatalog
 * (flow 12) and must stay in step with it — ingestion accepts unknown codes over HTTP and
 * then silently discards them — so the codes live here and nowhere else.
 */
export const REPORT_ISSUE_FLOW = "report_issue";

export const REPORT_ISSUE_EVENTS = {
	opened: "report_issue_opened",
	started: "report_issue_started",
	submitted: "report_issue_submitted",
} as const;

/**
 * The seeded "EKEDC Issue Tracker" service request — the only one that counts as a report issue.
 * Spelled in lower case because that is how the backend serialises ids.
 */
export const ISSUE_TRACKER_SERVICE_REQUEST_ID = "e0000000-0000-0000-0000-000000000001";

export const isIssueTrackerServiceRequest = (serviceRequestId: string | null | undefined): boolean =>
	!!serviceRequestId && serviceRequestId.toLowerCase() === ISSUE_TRACKER_SERVICE_REQUEST_ID;

/** The Complaint Category the user entered, found by the field's placeholder like the prefill does. */
export const complaintCategoryFrom = (
	definition: ServiceRequestType | undefined,
	values: DynamicFieldItem[][]
): string | undefined => {
	if (!definition) return undefined;
	for (let index = 0; index < definition.sectionDefinitions.length; index++) {
		const attribute = (definition.sectionDefinitions[index].attributeDefinitions ?? []).find(
			item => item.placeholderText === "complaintCategory"
		);
		if (!attribute) continue;
		const field = (values[index] ?? []).find(item => item.fieldId === attribute.id);
		if (!field) return undefined;
		if (field.dropdownText) return field.dropdownText;
		return field.fieldValue === null || field.fieldValue === undefined ? undefined : String(field.fieldValue);
	}
	return undefined;
};

const track = (event: string, fields: Record<string, FieldValue>): void => {
	BusinessAnalytics.track(event, REPORT_ISSUE_FLOW, withoutEmpty(fields));
};

/** The Service Request page is on screen. `user_id` is the signed-in user's `nameid` claim, left out when nobody is signed in. */
export const trackReportIssueOpened = (): void => {
	track(REPORT_ISSUE_EVENTS.opened, { user_id: getCurrentUserId() });
};

/** The Issue Tracker complaint form is on screen. The category is picked inside the form, so it is only known when the complaint is submitted. */
export const trackReportIssueStarted = (category?: string | null): void => {
	track(REPORT_ISSUE_EVENTS.started, { category });
};

/** The complaint was accepted. `issueId` is left out when the submit response carries no id to report. */
export const trackReportIssueSubmitted = (category: string | null | undefined, issueId?: string | null): void => {
	track(REPORT_ISSUE_EVENTS.submitted, { category, issue_id: issueId });
};
